import { useMemo } from 'react';
import { isCurrencyField, normalizeCurrencyValue } from '../../utils/currency';

// Job status configuration - defines behavior for each status
const JOB_STATUS_CONFIG = {
	open: {
		displayName: 'Open',
		color: 'info',
		canEdit: true,
		canSave: true,
		showEditButton: true,
		showUserSubmittedValues: true,
		message: 'Job is being created and can be edited'
	},
	pending: {
		displayName: 'Review',
		color: 'warning', 
		canEdit: true,
		canSave: true,
		showEditButton: true,
		showUserSubmittedValues: true,
		message: 'Job is ready for review and can still be edited'
	},
	submitted: {
		displayName: 'Submitted',
		color: 'success',
		canEdit: false,
		canSave: false,
		showEditButton: false,
		showUserSubmittedValues: false,
		message: 'Job has been submitted to NCS and cannot be edited'
	},
	archived: {
		displayName: 'Archived',
		color: 'secondary',
		canEdit: false,
		canSave: false,
		showEditButton: false,
		showUserSubmittedValues: false,
		message: 'Job has been archived and cannot be edited'
	},
	deleted: {
		displayName: 'Deleted',
		color: 'danger',
		canEdit: false,
		canSave: false,
		showEditButton: false,
		showUserSubmittedValues: false,
		message: 'Job has been deleted'
	}
};

/**
 * Custom hook to manage job status logic
 * @param {string} status - The job status
 * @param {boolean} readonly - Whether the component is in readonly mode
 * @param {boolean} userApiKey - Whether the user has an API key
 * @returns {object} Status configuration and helper functions
 */
export const useJobStatus = (status = 'open', readonly = true, userApiKey = false) => {
	
	const statusConfig = useMemo(() => {
		return JOB_STATUS_CONFIG[status] || JOB_STATUS_CONFIG.open;
	}, [status]);

	/**
	 * Determine if a field should be editable based on status and user permissions
	 * @param {string} fieldStatus - Optional field-specific status override
	 * @returns {boolean} Whether the field should be editable
	 */
	const isFieldEditable = useMemo(() => {
		return (fieldStatus = status) => {
			const config = JOB_STATUS_CONFIG[fieldStatus] || JOB_STATUS_CONFIG.open;
			return config.canEdit && !readonly && userApiKey;
		};
	}, [status, readonly, userApiKey]);

	/**
	 * Determine if user submitted values should be shown
	 * @param {string} userSubmittedValue - The user submitted value
	 * @param {string} currentValue - The current value
	 * @param {string} fieldStatus - Optional field-specific status override
	 * @returns {boolean} Whether to show user submitted values
	 */
	const shouldShowUserSubmitted = useMemo(() => {
		return (userSubmittedValue, currentValue, fieldStatus = status) => {
			const config = JOB_STATUS_CONFIG[fieldStatus] || JOB_STATUS_CONFIG.open;
			
			if (!config.showUserSubmittedValues || !userSubmittedValue || userSubmittedValue === '') {
				return false;
			}
			
			// For currency fields, normalize values before comparison
			if (isCurrencyField(userSubmittedValue)) {
				const normalizedUserValue = normalizeCurrencyValue(userSubmittedValue);
				const normalizedCurrentValue = normalizeCurrencyValue(currentValue);
				
				// Don't show user-submitted value if it's 0.00
				if (normalizedUserValue === '0.00') {
					return false;
				}
				
				return normalizedUserValue !== normalizedCurrentValue;
			}
			
			return userSubmittedValue !== currentValue;
		};
	}, [status]);

	/**
	 * Get status display information
	 * @returns {object} Status display configuration
	 */
	const getStatusDisplay = useMemo(() => {
		return {
			name: statusConfig.displayName,
			color: statusConfig.color,
			message: statusConfig.message
		};
	}, [statusConfig]);

	/**
	 * Check if user can perform specific actions
	 * @returns {object} Action permissions
	 */
	const canPerformActions = useMemo(() => {
		return {
			edit: statusConfig.canEdit,
			save: statusConfig.canSave,
			showEditButton: statusConfig.showEditButton,
			showUserSubmittedValues: statusConfig.showUserSubmittedValues
		};
	}, [statusConfig]);

	return {
		statusConfig,
		isFieldEditable,
		shouldShowUserSubmitted,
		getStatusDisplay,
		canPerformActions
	};
};

export default useJobStatus; 